/**
 * Prerekvizity importu/založení projektu v GitLabu.
 *
 * Proč karta a ne hláška až na konci (Filip 2026-09-13): průvodce se ptal na
 * stack a teprve po odeslání řekl, že na repozitář nemáš práva — nebo že v
 * GitLabu vůbec nesmíš zakládat projekty. Krok PREREKVIZITY se ptá první: pod
 * jakou identitou jednáš, co v tom repozitáři smíš, kdo to může schválit a co
 * se vlastně má stát (na místě / klon / fork). Autoritou je
 * `orchestrator.project_preflight` — UI ta pravidla nepočítá znovu, jen je
 * ukazuje.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Select,
} from '../../../components/ui';

export type ImportMode = 'existing' | 'clone' | 'fork';

/** Jeden nález pre-flightu; `details` nese to, co UI umí zobrazit lidsky. */
export interface PreflightFinding {
  code: string;
  message: string;
  service?: string | null;
  node?: string | null;
  details?: {
    permissions?: {
      role?: string | null;
      can_read?: boolean;
      can_push_default_branch?: boolean;
      default_branch?: string | null;
    };
    approvers?: Array<{ id: number; name?: string; username: string }>;
    /** Ověřený seznam (párování identit), nebo jen maintaineři z metadat. */
    approvers_verified?: boolean;
    username?: string | null;
    namespace?: string | null;
    mode?: string;
  };
}

export interface PreflightResp {
  ok?: boolean;
  fits?: boolean;
  verdict?: string;
  budget?: { total_human?: string; unpriced?: string[] };
  quota?: { remaining_human?: string; limit_human?: string; environments?: number };
  blockers?: PreflightFinding[];
  warnings?: PreflightFinding[];
  checked?: string[];
  error?: string;
}

interface AccessResp {
  ok: boolean;
  error?: string;
  code?: string;
  permissions?: {
    can_read: boolean;
    can_push_default_branch: boolean;
    role?: string | null;
    default_branch?: string | null;
  };
  approvers?: Array<{ id: number; name?: string; username: string }>;
}

interface IdentityResp {
  ok: boolean;
  error?: string;
  code?: string;
  username?: string | null;
  namespace?: string | null;
  can_create_project?: boolean | null;
  connection?: { id: string; host: string };
  actor?: { gitlab_username?: string };
}

/** Repozitářové prerekvizity; kapacita uzlu a kvóta jsou jiný krok průvodce. */
export function isGitlabFinding(finding: PreflightFinding): boolean {
  return finding.code.startsWith('gitlab_');
}

/**
 * Známé kódy z GitLab modulu → co s tím a kam jít. Bez toho UI ukazovalo holý
 * `GITLAB_SSO_ACCOUNT_MISSING` a člověk neměl šanci uhodnout, že se párují
 * identity v modulu GitLab (Filip 2026-09-13).
 */
const KNOWN_GITLAB_CODES = [
  'GITLAB_PERSONAL_IDENTITY_REQUIRED',
  'GITLAB_SSO_ACCOUNT_MISSING',
  'GITLAB_SSO_IDENTITY_MISMATCH',
  'GITLAB_DELEGATION_NOT_CONFIGURED',
  'GITLAB_CONNECTION_SELECTION_REQUIRED',
] as const;

export function gitlabCodeHint(
  raw: string | undefined | null,
  t: (key: string, fallback: string) => string,
): string | null {
  // Kód přijde buď v poli `code`, nebo (u vyhozené chyby) jako text hlášky —
  // obojí je pro člověka stejně nečitelné, tak se hledá v obou.
  const text = typeof raw === 'string' ? raw : '';
  const code = KNOWN_GITLAB_CODES.find((c) => text === c || text.includes(c));
  switch (code) {
    case 'GITLAB_PERSONAL_IDENTITY_REQUIRED':
      return t(
        'codable.glCodePersonalIdentity',
        'Tohle jde jen pod osobní identitou, ne pod systémovým klíčem. Přihlas se svým účtem a zkus to znovu.',
      );
    case 'GITLAB_SSO_ACCOUNT_MISSING':
    case 'GITLAB_SSO_IDENTITY_MISMATCH':
      return t(
        'codable.glCodeSsoAccountMissing',
        'K tvé přihlašovací identitě se v GitLabu nenašel jednoznačný účet. Spáruj identity: modul GitLab → Uživatelé → párování identit.',
      );
    case 'GITLAB_DELEGATION_NOT_CONFIGURED':
      return t(
        'codable.glCodeDelegationMissing',
        'Propojení ACM s GitLabem není dokončené. Správce ho dodělá v modulu GitLab → Uživatelé → párování identit.',
      );
    case 'GITLAB_CONNECTION_SELECTION_REQUIRED':
      return t(
        'codable.glCodeConnectionSelection',
        'Je nastavených víc GitLabů. Vyber nahoře, se kterým se má pracovat.',
      );
    default:
      return null;
  }
}

export function GitlabConnectionSelect(
  { value, onChange }: { value: string; onChange: (id: string) => void },
) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['gitlab-caller-connections'],
    staleTime: 30_000,
    queryFn: () =>
      api.mcp<
        {
          ok: boolean;
          connections: Array<{ id: string; host: string; ready: boolean; error?: string }>;
        }
      >('gitlab.project', { action: 'connections' }),
  });
  const connections = q.data?.connections ?? [];
  useEffect(() => {
    if (!value && connections.length === 1) onChange(connections[0]!.id);
  }, [connections, value, onChange]);
  return (
    <label className='block text-sm space-y-1'>
      GitLab
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value=''>{t('codable.chooseGitlab', 'Vyber GitLab')}</option>
        {connections.map((c) => (
          <option key={c.id} value={c.id} disabled={!c.ready}>
            {c.host}
            {c.ready ? '' : ` — ${c.error}`}
          </option>
        ))}
      </Select>
      {q.error && <span role='alert' className='text-amber-300'>{q.error.message}</span>}
    </label>
  );
}

/**
 * Pre-flight pro průvodce. Stejný `queryKey` v kartě prerekvizit i v kartě
 * stacku = jedno volání a jedna odpověď; jinak by se dvě karty ptaly zvlášť a
 * mohly by si odporovat.
 */
export function useProjectPreflight(params: {
  mode: ImportMode | 'create';
  backend: string;
  frontend: string;
  projectId?: number | null;
  connection?: string | null;
  enabled?: boolean;
}): UseQueryResult<PreflightResp> {
  return useQuery({
    queryKey: [
      'project-preflight',
      params.backend,
      params.frontend,
      params.projectId ?? null,
      params.connection ?? null,
      params.mode,
    ],
    enabled: params.enabled !== false,
    staleTime: 10_000,
    queryFn: () =>
      api.mcp<PreflightResp>('orchestrator.project_preflight', {
        backend: params.backend,
        frontend: params.frontend,
        repository: {
          mode: params.mode,
          ...(params.projectId ? { project_id: params.projectId } : {}),
          ...(params.connection ? { connection_id: params.connection } : {}),
        },
      }),
  });
}

function useProjectAccess(project: number | string | undefined, connection?: string) {
  return useQuery({
    queryKey: ['gitlab-import-access', connection, project],
    enabled: project !== undefined && project !== '',
    staleTime: 15_000,
    queryFn: () =>
      api.mcp<AccessResp>('gitlab.project', {
        action: 'access',
        project: String(project),
        connection_id: connection,
      }),
  });
}

function ImportModeSelect(
  { mode, onMode }: { mode: ImportMode; onMode: (mode: ImportMode) => void },
) {
  const { t } = useTranslation();
  return (
    <label className='block text-sm space-y-1'>
      {t('codable.importMode', 'Způsob importu')}
      <Select value={mode} onChange={(e) => onMode(e.target.value as ImportMode)}>
        <option value='existing'>
          {t('codable.importInPlace', 'Na místě — stávající repozitář')}
        </option>
        <option value='clone'>{t('codable.importClone', 'Samostatný klon v mém účtu')}</option>
        <option value='fork'>{t('codable.importFork', 'Fork v mém účtu')}</option>
      </Select>
    </label>
  );
}

/**
 * Práva volajícího v repozitáři + cesta ven, když nestačí.
 *
 * Žádost o schválení má vlastní stav: člověk odsud odejde a vrátí se, až mu
 * práva někdo dá — průvodce mu to musí říct, ne jen blikat chybou.
 */
function ProjectAccessPanel({ project, connection, mode, accessQ, onPendingApproval }: {
  project: number | string;
  connection?: string;
  mode: ImportMode;
  accessQ: UseQueryResult<AccessResp>;
  onPendingApproval?: () => void;
}) {
  const { t } = useTranslation();
  const request = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; state?: string; error?: string; code?: string; warning?: string }>(
        'gitlab.project',
        { action: 'request_access', project: String(project), connection_id: connection, send: true },
      ),
    onSuccess: (r) => {
      if (r.ok || r.state === 'pending_approval') onPendingApproval?.();
    },
  });
  const q = accessQ;
  const permissions = q.data?.permissions;
  const hint = gitlabCodeHint(q.data?.code ?? q.data?.error ?? q.error?.message, t);
  const pending = request.data?.ok === true || request.data?.state === 'pending_approval';
  // Klon a fork vznikají v účtu volajícího — do zdroje se nezapisuje, takže
  // varování o právu zápisu by tu bylo strašení bez důvodu.
  const writesToSource = mode === 'existing';
  return (
    <div className='space-y-2 text-sm'>
      {q.isFetching && <p>{t('common.loading', 'Načítám…')}</p>}
      {(q.error || q.data?.ok === false) && (
        <p role='alert' className='text-amber-300'>
          {hint ?? q.error?.message ?? q.data?.error}
        </p>
      )}
      {permissions && (
        <p>
          {t('codable.gitlabCurrentRole', 'Tvoje práva v GitLabu: {{role}}.', {
            role: permissions.role ?? 'read',
          })}
        </p>
      )}
      {!writesToSource && permissions?.can_read && (
        <p className='text-gray-400'>
          {t(
            'codable.importCopyNoWrite',
            'Klon i fork vzniknou v tvém účtu — do zdrojového repozitáře se nezapisuje, právo zápisu tedy nepotřebuješ.',
          )}
        </p>
      )}
      {writesToSource && permissions?.can_read && !permissions.can_push_default_branch && (
        <>
          <p className='text-amber-300'>
            {t(
              'codable.readOnlyImport',
              'Import je povolen. Pro zápis do hlavní větve potřebuješ další práva nebo vlastní klon či fork.',
            )}
          </p>
          <p>{t('codable.importApprovers', 'Vlastníci a lidé s právem zápisu do hlavní větve:')}</p>
          <ul>{q.data?.approvers?.map((p) => <li key={p.id}>{p.name ?? p.username}</li>)}</ul>
          {!pending && (
            <Button
              variant='secondary'
              size='sm'
              disabled={request.isPending}
              onClick={() => request.mutate()}
            >
              {t('codable.sendAccessRequest', 'Odeslat žádost ke schválení')}
            </Button>
          )}
        </>
      )}
      {pending && (
        <Alert variant='info'>
          <AlertTitle>{t('codable.requestPending', 'Žádost čeká na schválení.')}</AlertTitle>
          <AlertDescription>
            {t(
              'codable.requestPendingReturn',
              'Po schválení se sem vrať — vybraný projekt i způsob importu ti průvodce nechá.',
            )}
          </AlertDescription>
        </Alert>
      )}
      {request.data?.warning && <p role='alert' className='text-amber-300'>{request.data.warning}</p>}
      {(request.error || (request.data?.ok === false && !pending)) && (
        <p role='alert' className='text-amber-300'>
          {gitlabCodeHint(request.data?.code ?? request.data?.error ?? request.error?.message, t) ??
            request.error?.message ?? request.data?.error}
        </p>
      )}
    </div>
  );
}

/**
 * Kompaktní varianta (výběr módu + práva) pro seznamy, kde se rozhoduje o víc
 * projektech naráz — karta prerekvizit je na to moc velká.
 */
export function GitlabImportAccess({ project, connection, mode, onMode }: {
  project: number | string;
  connection?: string;
  mode: ImportMode;
  onMode: (mode: ImportMode) => void;
}) {
  const accessQ = useProjectAccess(project, connection);
  return (
    <div className='rounded border border-gray-700 p-3 space-y-2 text-sm'>
      <ImportModeSelect mode={mode} onMode={onMode} />
      <ProjectAccessPanel
        project={project}
        connection={connection}
        mode={mode}
        accessQ={accessQ}
      />
    </div>
  );
}

/**
 * Krok PREREKVIZITY — první karta průvodce po výběru projektu (nebo po výběru
 * spojení u nového projektu). Dokud tu je blokátor, další krok se nepouští.
 */
export function GitlabPrerequisites({
  project,
  projectPath,
  connection,
  mode,
  onMode,
  preflight,
  onPendingApproval,
  action,
  onClose,
}: {
  /** Zdrojový projekt; bez něj jde o zakládání nového (mód `create`). */
  project?: number | string;
  projectPath?: string;
  connection?: string;
  mode: ImportMode | 'create';
  onMode?: (mode: ImportMode) => void;
  preflight: UseQueryResult<PreflightResp>;
  onPendingApproval?: () => void;
  /** Tlačítko dalšího kroku; blokátor ho zhasne i s důvodem. */
  action?: { label: string; onClick: () => void; loading?: boolean };
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const identityQ = useQuery({
    queryKey: ['gitlab-caller-identity', connection ?? null],
    staleTime: 30_000,
    queryFn: () =>
      api.mcp<IdentityResp>('gitlab.project', {
        action: 'identity',
        ...(connection ? { connection_id: connection } : {}),
      }),
  });
  const accessQ = useProjectAccess(project, connection);
  const identity = identityQ.data;
  const blockers = (preflight.data?.blockers ?? []).filter(isGitlabFinding);
  const identityHint = gitlabCodeHint(
    identity?.code ?? identity?.error ?? identityQ.error?.message,
    t,
  );
  const blocked = blockers.length > 0;
  return (
    <Card>
      <CardContent>
        <div className='space-y-4'>
          <div className='flex items-start justify-between gap-3'>
            <div className='min-w-0'>
              <h3 className='text-sm font-semibold text-gray-100'>
                {t('codable.prereqTitle', 'Prerekvizity')}
              </h3>
              <p className='mt-1 text-xs text-gray-500'>
                {t(
                  'codable.prereqIntro',
                  'Než se sáhne na repozitář: pod jakou identitou jednáš a co s ním smíš.',
                )}
              </p>
              {projectPath && (
                <p className='mt-1 truncate font-mono text-xs text-gray-500'>{projectPath}</p>
              )}
            </div>
            {onClose && (
              <Button variant='ghost' size='sm' onClick={onClose}>
                {t('common.close', 'Zavřít')}
              </Button>
            )}
          </div>

          <div className='space-y-1 text-sm'>
            {identityQ.isLoading && <p>{t('common.loading', 'Načítám…')}</p>}
            {identity?.ok && (
              <p>
                {t('codable.prereqIdentity', 'Jednáš jako {{user}} na {{host}}.', {
                  user: identity.username ?? identity.actor?.gitlab_username ?? '—',
                  host: identity.connection?.host ?? '—',
                })}
              </p>
            )}
            {identity?.ok && identity.can_create_project === true && (
              <p className='text-gray-400'>
                {t('codable.prereqCanCreate', 'Smíš zakládat projekty pod {{namespace}}.', {
                  namespace: identity.namespace ?? identity.username ?? '—',
                })}
              </p>
            )}
            {(identityQ.error || identity?.ok === false) && (
              <p role='alert' className='text-amber-300'>
                {identityHint ?? identityQ.error?.message ?? identity?.error}
              </p>
            )}
          </div>

          {project !== undefined && onMode && (
            <ImportModeSelect mode={mode === 'create' ? 'existing' : mode} onMode={onMode} />
          )}

          {project !== undefined && (
            <ProjectAccessPanel
              project={project}
              connection={connection}
              mode={mode === 'create' ? 'existing' : mode}
              accessQ={accessQ}
              onPendingApproval={onPendingApproval}
            />
          )}

          {preflight.isFetching && !preflight.data && (
            <p className='text-sm text-gray-400'>
              {t('codable.prereqChecking', 'Ověřuju prerekvizity…')}
            </p>
          )}
          {blockers.map((b) => (
            <Alert key={b.code} variant='destructive'>
              <AlertTitle>{t('codable.prereqBlocked', 'Takhle to dál nepůjde')}</AlertTitle>
              <AlertDescription>{b.message}</AlertDescription>
            </Alert>
          ))}

          {action && (
            <div className='flex flex-wrap items-center gap-3'>
              <Button
                variant='primary'
                onClick={action.onClick}
                loading={action.loading}
                disabled={blocked || preflight.isFetching || action.loading}
              >
                {action.label}
              </Button>
              {blocked && (
                <span className='text-xs text-amber-300'>
                  {t('codable.prereqBlockedHint', 'Nejdřív vyřeš prerekvizity výše.')}
                </span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function ProjectWorkerChoice({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const [skip, setSkip] = useState(false);
  const mutation = useMutation({
    mutationFn: (register: boolean) =>
      api.mcp<{ ok: boolean; state?: string; error?: string; code?: string; warning?: string }>(
        'gitlab.project',
        { action: 'worker', slug, register, send: register },
      ),
  });
  // Worker si projekt sám nepostaví — když na uzlu žádný není, je to úkol pro
  // správce, ne chyba uživatele (Filip 2026-09-13). Starší odpovědi kód nenesou,
  // proto i záchyt podle hlášky.
  const unavailable = mutation.data?.code === 'WORKER_UNAVAILABLE' ||
    (mutation.data?.error ?? '').includes('worker není k dispozici');
  const pending = mutation.data?.state === 'pending_approval';
  const workerRequest = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string; code?: string }>('gitlab.worker_request', {
        slug,
        kind: 'node',
      }),
  });
  return (
    <div className='rounded border border-gray-800 p-4 space-y-3'>
      <h3 className='font-medium'>
        {t('codable.registerWorkerQuestion', 'Zapojit build worker pro tento projekt?')}
      </h3>
      {!skip && !mutation.data?.ok && !unavailable && (
        <div className='flex gap-2'>
          <Button loading={mutation.isPending} onClick={() => mutation.mutate(true)}>
            {t('common.yes', 'Ano')}
          </Button>
          <Button
            variant='secondary'
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate(false, {
                onSuccess: (r) => {
                  if (r.ok) setSkip(true);
                },
              })}
          >
            {t('common.no', 'Ne')}
          </Button>
        </div>
      )}
      {skip && <p>{t('codable.workerSkipped', 'Worker nyní nezapojujeme.')}</p>}
      {unavailable && (
        <Alert variant='warning'>
          <AlertTitle>
            {t('codable.workerUnavailable', 'Pro tenhle projekt zatím není build worker.')}
          </AlertTitle>
          <AlertDescription>
            <p>
              {t(
                'codable.workerUnavailableHint',
                'Worker přidělí správce platformy — projekt se do té doby postaví, jen se nebude buildit v CI.',
              )}
            </p>
            {workerRequest.data?.ok
              ? (
                <p role='status' className='mt-2'>
                  {t('codable.workerRequestSent', 'Žádost o worker odešla správci.')}
                </p>
              )
              : (
                <Button
                  variant='secondary'
                  size='sm'
                  className='mt-2'
                  loading={workerRequest.isPending}
                  onClick={() => workerRequest.mutate()}
                >
                  {t('codable.workerRequestAdmin', 'Požádat správce o worker')}
                </Button>
              )}
            {(workerRequest.error || workerRequest.data?.ok === false) && (
              <p role='alert' className='mt-2 text-amber-300'>
                {gitlabCodeHint(
                  workerRequest.data?.code ?? workerRequest.data?.error ??
                    workerRequest.error?.message,
                  t,
                ) ?? workerRequest.error?.message ??
                  workerRequest.data?.error}
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}
      {mutation.data?.warning && (
        <p role='alert' className='text-amber-300'>{mutation.data.warning}</p>
      )}
      {pending && (
        <p role='status'>{t('codable.requestPending', 'Žádost čeká na schválení.')}</p>
      )}
      {mutation.data?.ok && !skip && !pending && (
        <p role='status'>{t('codable.workerRegistered', 'Worker je zapojený.')}</p>
      )}
      {(mutation.error || (mutation.data?.ok === false && !unavailable && !pending)) && (
        <p role='alert' className='text-amber-300'>
          {gitlabCodeHint(
            mutation.data?.code ?? mutation.data?.error ?? mutation.error?.message,
            t,
          ) ?? mutation.error?.message ??
            mutation.data?.error}
        </p>
      )}
    </div>
  );
}
